import { z } from 'zod';
export const documentsTrainingRecordArtifactsCapability = 'documents.training-record-artifacts.v1';
export const documentsTrainingRecordFactsProviderCapability = 'training.documents-record-facts.v1';
export const documentsTrainingRecordFactsSlot = 'documents.training-record-facts';
export const documentsTrainingRecordFactsProviderId = 'training.server.documents-record-facts';
export const documentsTrainingRecordKindSchema = z.enum([
    'transcript',
    'internal-completion-certificate',
]);
export const documentsTrainingCourseSelectionSchema = z.strictObject({
    courseId: z.string().uuid(),
    releaseId: z.string().uuid(),
});
export const documentsTrainingRecordFactsRequestSchema = z.strictObject({
    consumerModuleId: z.literal('documents'),
    requestedCapability: z.literal(documentsTrainingRecordArtifactsCapability),
    resourceType: z.enum([
        'documents.training-record-artifact',
        'documents.training-record-options',
    ]),
    resourceId: z.string().uuid(),
    recordKind: documentsTrainingRecordKindSchema,
    membershipId: z.string().uuid(),
    selectedCourse: documentsTrainingCourseSelectionSchema.nullable(),
    includeProfessionalCertifications: z.boolean(),
    maximumInternalCompletions: z.number().int().min(1).max(500),
    maximumProfessionalCertifications: z.number().int().min(0).max(500),
    asOf: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    if (value.recordKind === 'internal-completion-certificate') {
        if (value.selectedCourse === null)
            context.addIssue({
                code: 'custom', path: ['selectedCourse'],
                message: 'An internal completion certificate requires one exact course release.',
            });
        if (value.includeProfessionalCertifications)
            context.addIssue({
                code: 'custom', path: ['includeProfessionalCertifications'],
                message: 'An internal completion certificate cannot include outside credentials.',
            });
        if (value.maximumInternalCompletions !== 1)
            context.addIssue({
                code: 'custom', path: ['maximumInternalCompletions'],
                message: 'An internal completion certificate resolves exactly one completion.',
            });
    }
    else if (value.selectedCourse !== null)
        context.addIssue({
            code: 'custom', path: ['selectedCourse'],
            message: 'A transcript includes the learner record rather than one selected course.',
        });
    if (value.includeProfessionalCertifications !==
        (value.maximumProfessionalCertifications > 0))
        context.addIssue({
            code: 'custom', path: ['maximumProfessionalCertifications'],
            message: 'The outside-credential limit must match the requested transcript content.',
        });
});
export const documentsTrainingInternalAssessmentFactSchema = z.strictObject({
    attemptId: z.string().uuid(),
    attemptNumber: z.number().int().positive(),
    scoreBasisPoints: z.number().int().min(0).max(10_000),
    passed: z.literal(true),
    submittedAt: z.string().datetime({ offset: true }),
    assessmentSnapshotHash: z.string().regex(/^[a-f0-9]{64}$/),
});
export const documentsTrainingInternalCompletionFactSchema = z.strictObject({
    courseId: z.string().uuid(),
    courseSlug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    releaseId: z.string().uuid(),
    releaseVersion: z.number().int().positive(),
    courseTitle: z.string().trim().min(1).max(250),
    courseOrigin: z.enum(['platform-release', 'tenant-release']),
    releaseContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    completionState: z.literal('completed'),
    completionPercentage: z.literal(100),
    completedAt: z.string().datetime({ offset: true }),
    internalCompletionNotice: z.string().trim().min(20).max(500),
    assessment: documentsTrainingInternalAssessmentFactSchema.nullable(),
    authorityBasis: z.literal('crest-tenant-internal-training-only'),
});
export const documentsTrainingProfessionalCertificationFactSchema = z.strictObject({
    certificationId: z.string().uuid(),
    certificationName: z.string().trim().min(2).max(240),
    issuerName: z.string().trim().min(2).max(240),
    credentialNumber: z.string().trim().min(1).max(240).nullable(),
    issuedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    expiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    lifecycleStatus: z.enum(['current', 'superseded', 'revoked']),
    statusAsOf: z.enum(['current', 'expired', 'non-expiring', 'superseded', 'revoked']),
    recordBasis: z.literal('tenant-attested'),
    issuerVerification: z.literal('not-verified-by-crest'),
});
export const documentsTrainingRecordFactsPresentSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(documentsTrainingRecordFactsProviderCapability),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    recordKind: documentsTrainingRecordKindSchema,
    asOf: z.string().datetime({ offset: true }),
    internalCompletionAuthority: z.literal('crest-tenant-internal-training-only'),
    professionalCertificationAuthority: z.literal('tenant-attested-not-verified-by-crest'),
    internalCompletions: z.array(documentsTrainingInternalCompletionFactSchema).max(500),
    internalCompletionTotal: z.number().int().nonnegative().safe(),
    internalCompletionsTruncated: z.boolean(),
    professionalCertifications: z.array(documentsTrainingProfessionalCertificationFactSchema).max(500),
    professionalCertificationTotal: z.number().int().nonnegative().safe(),
    professionalCertificationsTruncated: z.boolean(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
}).superRefine((value, context) => {
    if (value.internalCompletionTotal < value.internalCompletions.length ||
        value.internalCompletionsTruncated !==
            (value.internalCompletionTotal > value.internalCompletions.length))
        context.addIssue({
            code: 'custom', path: ['internalCompletionTotal'],
            message: 'Internal completion totals must reconcile with the returned facts.',
        });
    if (value.professionalCertificationTotal < value.professionalCertifications.length ||
        value.professionalCertificationsTruncated !==
            (value.professionalCertificationTotal > value.professionalCertifications.length))
        context.addIssue({
            code: 'custom', path: ['professionalCertificationTotal'],
            message: 'Professional credential totals must reconcile with the returned facts.',
        });
    if (value.recordKind === 'internal-completion-certificate' &&
        (value.internalCompletions.length !== 1 ||
            value.internalCompletionTotal !== 1 ||
            value.professionalCertifications.length !== 0 ||
            value.professionalCertificationTotal !== 0))
        context.addIssue({
            code: 'custom', path: ['recordKind'],
            message: 'An internal completion certificate contains exactly one internal completion.',
        });
});
export const documentsTrainingRecordFactsNotFoundSchema = z.strictObject({
    state: z.literal('not-found'),
    capability: z.literal(documentsTrainingRecordFactsProviderCapability),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    recordKind: z.literal('internal-completion-certificate'),
    asOf: z.string().datetime({ offset: true }),
    reason: z.literal('internal-completion-not-found'),
});
export const documentsTrainingRecordFactsResponseSchema = z.discriminatedUnion('state', [
    documentsTrainingRecordFactsPresentSchema,
    documentsTrainingRecordFactsNotFoundSchema,
]);
export const documentsTrainingRecordArtifactIdSchema = z.string().uuid();
export const documentsTrainingRecordCommandIdSchema = z.string().uuid();
export const documentsTrainingRecordPageSchema = z.strictObject({
    format: z.enum(['letter', 'legal', 'a4']),
    orientation: z.enum(['portrait', 'landscape']),
    marginPreset: z.enum(['compact', 'standard', 'wide']),
});
export const documentsTrainingRecordRenderSchema = z.strictObject({
    bodyHtml: z.string().trim().min(1).max(1_000_000),
    bodyHtmlSha256: z.string().regex(/^[a-f0-9]{64}$/),
    printableHtml: z.string().trim().min(1).max(1_500_000),
    printableHtmlSha256: z.string().regex(/^[a-f0-9]{64}$/),
    sourceContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    page: documentsTrainingRecordPageSchema,
    contentType: z.literal('text/html; charset=utf-8'),
});
export const documentsCreateTrainingRecordArtifactSchema = z.strictObject({
    commandId: documentsTrainingRecordCommandIdSchema,
    kind: documentsTrainingRecordKindSchema,
    membershipId: z.string().uuid(),
    selectedCourse: documentsTrainingCourseSelectionSchema.nullable().default(null),
    includeProfessionalCertifications: z.boolean().nullable().default(null),
    reason: z.string().trim().min(5).max(500),
}).superRefine((value, context) => {
    if (value.kind === 'internal-completion-certificate') {
        if (value.selectedCourse === null)
            context.addIssue({
                code: 'custom', path: ['selectedCourse'],
                message: 'Select one completed course release for the internal certificate.',
            });
        if (value.includeProfessionalCertifications === true)
            context.addIssue({
                code: 'custom', path: ['includeProfessionalCertifications'],
                message: 'Outside credentials cannot appear on an internal completion certificate.',
            });
    }
    else if (value.selectedCourse !== null)
        context.addIssue({
            code: 'custom', path: ['selectedCourse'],
            message: 'A transcript uses the complete available learner record.',
        });
});
export const documentsTrainingRecordArtifactSummarySchema = z.strictObject({
    contractVersion: z.literal(documentsTrainingRecordArtifactsCapability),
    tenantId: z.string().uuid(),
    id: documentsTrainingRecordArtifactIdSchema,
    commandId: documentsTrainingRecordCommandIdSchema,
    kind: documentsTrainingRecordKindSchema,
    membershipId: z.string().uuid(),
    subjectDisplayName: z.string().trim().min(1).max(240),
    sourceAsOf: z.string().datetime({ offset: true }),
    sourceContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    internalCompletionCount: z.number().int().nonnegative().safe(),
    professionalCertificationCount: z.number().int().nonnegative().safe(),
    selectedCourse: documentsTrainingCourseSelectionSchema.nullable(),
    requestHash: z.string().regex(/^[a-f0-9]{64}$/),
    printableHtmlSha256: z.string().regex(/^[a-f0-9]{64}$/),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const documentsTrainingRecordArtifactSchema = documentsTrainingRecordArtifactSummarySchema.extend({
    sourceSnapshot: documentsTrainingRecordFactsPresentSchema,
    render: documentsTrainingRecordRenderSchema,
    reason: z.string().trim().min(5).max(500),
}).superRefine((value, context) => {
    if (value.sourceSnapshot.tenantId !== value.tenantId ||
        value.sourceSnapshot.membershipId !== value.membershipId ||
        value.sourceSnapshot.recordKind !== value.kind ||
        value.sourceSnapshot.asOf !== value.sourceAsOf ||
        value.sourceSnapshot.contentHash !== value.sourceContentHash ||
        value.render.sourceContentHash !== value.sourceContentHash ||
        value.internalCompletionCount !== value.sourceSnapshot.internalCompletions.length ||
        value.professionalCertificationCount !==
            value.sourceSnapshot.professionalCertifications.length)
        context.addIssue({
            code: 'custom', path: ['sourceSnapshot'],
            message: 'The retained Training source and rendered artifact must reconcile exactly.',
        });
});
export const documentsTrainingRecordArtifactListQuerySchema = z.strictObject({
    membershipId: z.string().uuid().nullable().default(null),
    kind: documentsTrainingRecordKindSchema.nullable().default(null),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const documentsTrainingRecordOptionsRequestSchema = z.strictObject({
    membershipId: z.string().uuid(),
});
export const documentsTrainingRecordOptionSchema = z.strictObject({
    courseId: z.string().uuid(),
    releaseId: z.string().uuid(),
    courseTitle: z.string().trim().min(1).max(250),
    releaseVersion: z.number().int().positive(),
    completedAt: z.string().datetime({ offset: true }),
});
export const documentsTrainingRecordOptionsResponseSchema = z.strictObject({
    data: z.strictObject({
        membershipId: z.string().uuid(),
        asOf: z.string().datetime({ offset: true }),
        internalCompletions: z.array(documentsTrainingRecordOptionSchema).max(500),
        internalCompletionTotal: z.number().int().nonnegative().safe(),
        internalCompletionsTruncated: z.boolean(),
    }),
});
const documentsTrainingRecordCommandMetadataSchema = z.strictObject({
    commandId: documentsTrainingRecordCommandIdSchema,
    idempotent: z.boolean(),
});
export const documentsTrainingRecordArtifactResponseSchema = z.strictObject({
    data: documentsTrainingRecordArtifactSchema,
    command: documentsTrainingRecordCommandMetadataSchema.optional(),
});
export const documentsTrainingRecordArtifactsResponseSchema = z.strictObject({
    data: z.array(documentsTrainingRecordArtifactSummarySchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
//# sourceMappingURL=documents-training-record-artifact.contract.js.map